import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Toaster } from './ui/sonner';
import Header from './Header';
import HeroSection from './HeroSection';
import TrustStripSection from './TrustStripSection';
import ProblemSection from './ProblemSection';
import SolutionSection from './SolutionSection';
import HowItWorksSection from './HowItWorksSection';
import FeaturesSection from './FeaturesSection';
import IITCredibilitySection from './IITCredibilitySection';
import ExpertCounsellorsSection from './ExpertCounsellorsSection';
import TestimonialsSection from './TestimonialsSection';
import ConversionCtaBand from './ConversionCtaBand';
import SchoolsSection from './SchoolsSection';
import CounselingSection from './CounselingSection';
import FAQSection from './FAQSection';
import LeadCaptureSection from './LeadCaptureSection';
import Footer from './Footer';
import GoogleAnalytics from './GoogleAnalytics';
import FloatingHelp from './FloatingHelp';
import ChatWidget from './chat/ChatWidget';
import TryDemoInviteBand from './TryDemoInviteBand';
import { useAudience } from '../contexts/AudienceContext';
import PageSeo from '../seo/PageSeo';
import { trackEvent } from '@/lib/trackEvent';
import '../App.css';

const HOME_TITLE =
  'ChooseRightCareer | Career DNA for Class 9 to 12 students who want clarity without the noise';
const HOME_DESCRIPTION =
  'Discover what suits you beyond marks and streams. Honest questions, AI plus expert shaped insights, a clear PDF, and optional counsellors when you want a real conversation.';
const SCHOOLS_LANDING_TITLE =
  'ChooseRightCareer for schools | Career DNA pilots, bulk assessments, and rollout support';
const SCHOOLS_LANDING_DESCRIPTION =
  'Structured Career DNA for Class 9 to 12, consistent PDFs for families, cohort analytics for leadership, and a partner team that respects your academic calendar. Book a demo to map a pilot.';

const STUDENT_ORDER = [
  'hero',
  'trust',
  'tryInvite',
  'problem',
  'solution',
  'how',
  'features',
  'iit',
  'expert',
  'testimonials',
  'ctaRepeat',
  'schools',
  'counseling',
  'faq',
  'lead',
];

const SCHOOL_ORDER = [
  'hero',
  'schools',
  'features',
  'how',
  'iit',
  'testimonials',
  'ctaRepeat',
  'problem',
  'solution',
  'expert',
  'counseling',
  'faq',
  'lead',
];

const SECTION_DOM_ID = {
  hero: 'hero',
  trust: 'trust-strip',
  tryInvite: 'try-demo-invite',
  problem: 'problem',
  solution: 'solution',
  how: 'how-it-works',
  features: 'features',
  iit: 'iit-credibility',
  expert: 'expert-counsellors',
  testimonials: 'student-reviews',
  ctaRepeat: 'cta-repeat',
  schools: 'schools',
  counseling: 'counseling',
  faq: 'faq',
  lead: 'get-started',
};

export default function LandingContent() {
  const { audience } = useAudience();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const isSchoolsPath = pathname === '/schools';
  /* Bumped by school CTAs so SchoolsSection opens its form and scrolls to it. */
  const [schoolFormOpenSignal, setSchoolFormOpenSignal] = React.useState(0);

  /** After /try CTA: scroll to lead form once home is mounted */
  useEffect(() => {
    let fromTry = false;
    try {
      fromTry = sessionStorage.getItem('crc_scrollTo') === 'get-started';
      if (fromTry) sessionStorage.removeItem('crc_scrollTo');
    } catch {
      /* private mode */
    }
    const fromHash = typeof window !== 'undefined' && window.location.hash === '#get-started';
    if (!fromTry && !fromHash) return undefined;
    const t = window.setTimeout(() => {
      document.getElementById('get-started')?.scrollIntoView({ behavior: 'smooth' });
      if (fromHash) {
        window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}`);
      }
    }, 450);
    return () => window.clearTimeout(t);
  }, [pathname, audience]);

  /** Pay-first Career DNA: details → /payment → /assessment?flow=direct. Bare /assessment redirects to /start (school keeps ?flow=school). */
  const handleStartAssessment = () => {
    void trackEvent('cta_clicked', {
      meta: { surface: 'landing', audience, action: 'navigate_start' },
    });
    navigate('/start');
  };

  const handleBookDemo = () => {
    void trackEvent('cta_clicked', {
      meta: { surface: 'landing', audience, action: 'open_school_form' },
    });
    setSchoolFormOpenSignal((n) => n + 1);
  };

  const handleScrollToHow = () => {
    void trackEvent('cta_clicked', {
      meta: { surface: 'landing', audience, action: 'scroll_how_it_works' },
    });
    document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleBookSession = () => {
    void trackEvent('cta_clicked', {
      meta: { surface: 'landing', audience, action: 'scroll_get_started' },
    });
    const el = document.getElementById('get-started');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const handleFloatingForm = () => {
    void trackEvent('cta_clicked', {
      meta: { surface: 'landing', audience, action: 'floating_help_scroll' },
    });
    const id = audience === 'school' ? 'schools' : 'get-started';
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const renderSection = (key) => {
    if (key === 'tryInvite' && audience === 'school') return null;
    const domId = SECTION_DOM_ID[key];
    const inner = (() => {
      switch (key) {
        case 'hero':
          return (
            <HeroSection
              onStartAssessment={handleStartAssessment}
              onBookDemo={handleBookDemo}
              onScrollToHow={handleScrollToHow}
            />
          );
        case 'trust':
          return <TrustStripSection />;
        case 'tryInvite':
          return <TryDemoInviteBand />;
        case 'problem':
          return <ProblemSection />;
        case 'solution':
          return (
            <SolutionSection onGetStarted={handleStartAssessment} onBookDemo={handleBookDemo} />
          );
        case 'how':
          return <HowItWorksSection />;
        case 'features':
          return <FeaturesSection />;
        case 'iit':
          return <IITCredibilitySection />;
        case 'expert':
          return <ExpertCounsellorsSection />;
        case 'testimonials':
          return <TestimonialsSection />;
        case 'ctaRepeat':
          return (
            <ConversionCtaBand onStudentAction={handleStartAssessment} onSchoolAction={handleBookDemo} />
          );
        case 'schools':
          return <SchoolsSection openFormSignal={schoolFormOpenSignal} />;
        case 'counseling':
          return <CounselingSection onBookSession={handleBookSession} />;
        case 'faq':
          return <FAQSection />;
        case 'lead':
          return <LeadCaptureSection />;
        default:
          return null;
      }
    })();

    return (
      <div key={`${audience}-${key}`} id={domId} className="scroll-mt-[5.25rem] md:scroll-mt-24">
        {inner}
      </div>
    );
  };

  const order = audience === 'school' ? SCHOOL_ORDER : STUDENT_ORDER;

  return (
    <div className="App">
      <PageSeo
        title={isSchoolsPath ? SCHOOLS_LANDING_TITLE : HOME_TITLE}
        description={isSchoolsPath ? SCHOOLS_LANDING_DESCRIPTION : HOME_DESCRIPTION}
        path={pathname}
      />
      <GoogleAnalytics />
      <Toaster position="top-right" richColors />
      <Header onStartAssessment={handleStartAssessment} onBookDemo={handleBookDemo} />

      <a href="#main-content" className="skip-link">
        Skip to main content
      </a>

      <main
        id="main-content"
        tabIndex={-1}
        key={audience}
        className="audience-main-crossfade min-w-0 max-w-full outline-none"
      >
        {order.map((key) => renderSection(key))}
      </main>

      <Footer />
      <FloatingHelp onOpenLead={handleFloatingForm} />
      <ChatWidget />
    </div>
  );
}
